import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatAddressUnit } from "@/lib/formatters";
import { toast } from "sonner";

/**
 * Phase 4 · Jobs Deep Dedupe.
 * Two-panel admin page:
 *   1. Auto-heal button — batch-merges every SAFE group (loser has no
 *      photos / clock times / sweeper pin / completed_at).
 *   2. Review list — every RISKY group where both sides have real work;
 *      user picks the keeper.
 */
export default function AdminJobsDedupe() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [singleBusy, setSingleBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/jobs-dedupe/groups");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    }
  };
  useEffect(() => { load(); }, []);

  const doAutoHeal = async () => {
    if (!data) return;
    if (!window.confirm(`Auto-heal ${data.safe_count} safe groups?\n\nThis archives ${data.groups.filter(g => g.auto_safe).reduce((s, g) => s + g.losers.length, 0)} duplicate rows (all have no photos, clock times, or work data). Reversible via jobs_archive_dedupe collection.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/jobs-dedupe/auto-heal", { dry_run: false });
      toast.success(`Archived ${r.data.archived} duplicates across ${r.data.safe_groups} groups`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Auto-heal failed");
    } finally { setBusy(false); }
  };

  const doManualMerge = async (group) => {
    const keeperId = group.winner.id;
    const archiveIds = group.losers.map(l => l.id);
    if (!window.confirm(`Merge ${archiveIds.length} duplicate${archiveIds.length === 1 ? "" : "s"} of WO ${group.identity.value}?\n\nKeeper: ${group.winner.id.slice(-6)} · ${group.winner.status} · richness ${group.winner.richness}`)) return;
    setSingleBusy(group.winner.id);
    try {
      const r = await api.post("/admin/jobs-dedupe/merge", {
        keep_id: keeperId,
        archive_ids: archiveIds,
      });
      toast.success(`Archived ${r.data.archived}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed");
    } finally { setSingleBusy(null); }
  };

  const safeGroups = data ? data.groups.filter(g => g.auto_safe) : [];
  const riskyGroups = data ? data.groups.filter(g => !g.auto_safe) : [];

  return (
    <AdminShell>
      <div className="max-w-5xl mx-auto p-5" data-testid="jobs-dedupe">
        <div className="flex items-center gap-3 mb-1 flex-wrap">
          <h1 className="text-2xl font-bold text-[#0B1F3A] tracking-tight">Duplicate Jobs</h1>
          <span className="text-[11px] font-bold uppercase tracking-widest text-red-700 bg-red-100 px-2.5 py-1 rounded-full">
            Phase 4 · deep dedupe
          </span>
          <button onClick={load} data-testid="jd-reload"
            className="ml-auto text-xs font-bold text-[#008a8f] hover:text-[#0B1F3A] underline">
            Reload
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-6 max-w-2xl">
          Duplicate WO records happen when AppFolio/Kraaft re-imports the same work order. "Safe" duplicates are the empty copies (no photos, no clock times, no notes) that can be merged in one click. "Risky" duplicates need eyeballs — usually both copies have some work data.
        </p>

        {data === null && (
          <div className="text-sm text-slate-400 p-8 text-center" data-testid="jd-loading">Loading…</div>
        )}

        {data && data.groups.length === 0 && (
          <div className="bg-white rounded-2xl p-10 text-center shadow-sm" data-testid="jd-empty">
            <div className="text-5xl mb-3">🎉</div>
            <div className="text-lg font-bold text-[#0B1F3A]">No duplicate jobs.</div>
            <div className="text-sm text-slate-500 mt-1">Your jobs collection is clean.</div>
          </div>
        )}

        {data && data.groups.length > 0 && (
          <>
            {/* Auto-heal card */}
            <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-300 rounded-2xl p-5 mb-6" data-testid="jd-autoheal-card">
              <div className="flex items-start gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-1">
                    Safe · auto-heal
                  </div>
                  <div className="text-lg font-bold text-[#0B1F3A]">
                    {safeGroups.length} groups · {safeGroups.reduce((s, g) => s + g.losers.length, 0)} rows to archive
                  </div>
                  <div className="text-sm text-slate-600 mt-1">
                    Every loser row is completely empty — no clock times, no photos, no receipts, no sweeper pin. The keeper is either the version with work data OR the newest of matching empty rows.
                  </div>
                </div>
                <button onClick={doAutoHeal} disabled={busy || safeGroups.length === 0}
                  data-testid="jd-autoheal-btn"
                  className="h-11 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50 whitespace-nowrap">
                  {busy ? "Archiving…" : `Auto-heal ${safeGroups.length}`}
                </button>
              </div>
            </div>

            {/* Review list */}
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-red-700 flex items-center gap-2">
              Needs review · {riskyGroups.length} group{riskyGroups.length === 1 ? "" : "s"}
              {riskyGroups.length === 0 && <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full normal-case">All clear</span>}
            </div>
            <div className="flex flex-col gap-3" data-testid="jd-risky-list">
              {riskyGroups.map(g => (
                <RiskyGroupCard key={`${g.identity.kind}-${g.identity.value}`}
                  group={g}
                  busy={singleBusy === g.winner.id}
                  onMerge={() => doManualMerge(g)}/>
              ))}
              {safeGroups.length > 0 && riskyGroups.length === 0 && (
                <div className="text-xs text-slate-400 text-center p-4">
                  Only safe groups remain. Hit the Auto-heal button above.
                </div>
              )}
            </div>

            {/* Safe preview (collapsed) */}
            {safeGroups.length > 0 && (
              <details className="mt-6 bg-white rounded-2xl p-4 shadow-sm" data-testid="jd-safe-preview">
                <summary className="cursor-pointer text-xs font-bold uppercase tracking-widest text-slate-500">
                  See safe groups ({safeGroups.length})
                </summary>
                <div className="mt-3 flex flex-col gap-1 text-xs">
                  {safeGroups.slice(0, 50).map(g => (
                    <div key={`${g.identity.kind}-${g.identity.value}`} className="flex items-center gap-3 py-1.5 border-b border-slate-100 last:border-0">
                      <span className="font-mono text-[10px] text-slate-400 w-8 uppercase">{g.identity.kind}</span>
                      <span className="font-mono text-slate-700 truncate flex-1">{g.identity.value}</span>
                      <span className="text-slate-500 flex-shrink-0">{g.dup_count} copies → 1</span>
                    </div>
                  ))}
                  {safeGroups.length > 50 && (
                    <div className="pt-2 text-[10px] text-slate-400 text-center">+{safeGroups.length - 50} more</div>
                  )}
                </div>
              </details>
            )}
          </>
        )}

        <div className="mt-8 text-[11px] text-slate-400">
          <Link to="/admin/portfolio-merge" className="underline">Portfolio Merge (Phase 3)</Link>
          {" · "}
          <Link to="/admin" className="underline">Admin home</Link>
        </div>
      </div>
    </AdminShell>
  );
}

function RiskyGroupCard({ group, busy, onMerge }) {
  const rows = [group.winner, ...group.losers];
  return (
    <div className="bg-white rounded-2xl shadow-sm p-4" data-testid={`jd-group-${group.identity.value.replace(/[^a-zA-Z0-9]/g, "_")}`}>
      <div className="flex items-start gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">
            {group.identity.kind === "wo" ? "WO#" : "Address"}
          </div>
          <div className="text-lg font-bold text-[#0B1F3A] break-all">{group.identity.value}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {group.dup_count} copies · both have real work data — pick the keeper carefully
          </div>
        </div>
        <button onClick={onMerge} disabled={busy}
          data-testid={`jd-merge-${group.winner.id}`}
          className="h-9 px-4 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white text-xs font-bold disabled:opacity-50">
          {busy ? "Merging…" : `Keep ⭐ · archive ${group.losers.length}`}
        </button>
      </div>
      <div className="flex flex-col gap-1.5">
        {rows.map((r, i) => (
          <div key={r.id} className={`flex items-start gap-2.5 p-2.5 rounded-lg text-xs
            ${i === 0 ? "bg-emerald-50 border-2 border-emerald-300" : "bg-slate-50 border border-slate-200"}`}
            data-testid={`jd-row-${r.id}`}>
            <span className={`px-2 py-0.5 rounded font-bold text-[10px] flex-shrink-0
              ${i === 0 ? "bg-emerald-600 text-white" : "bg-slate-300 text-slate-700"}`}>
              {i === 0 ? "⭐ KEEP" : "ARCHIVE"}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#0B1F3A]">
                {formatAddressUnit(r.property_address, r.unit)} <span className="text-slate-400 font-mono text-[10px]">· {r.id.slice(-6)}</span>
              </div>
              <div className="text-slate-500 truncate mt-0.5">{r.title || "(no title)"}</div>
              <div className="text-slate-500 mt-1 flex flex-wrap gap-2">
                <span>status: <b>{r.status || "?"}</b></span>
                <span>rich: <b>{r.richness}</b></span>
                {r.clock_in_at && <span>· ⏱ clocked in</span>}
                {r.completed_at && <span>· ✅ completed</span>}
                {r.sweeper_pin && <span>· 📌 pinned</span>}
                {r.photo_count > 0 && <span>· 📸 {r.photo_count}</span>}
                {r.receipt_count > 0 && <span>· 🧾 {r.receipt_count}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
